import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, Copy, FileJson, ScanText, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useDialog } from '../lib/useDialog';
import { localeUI } from '../lib/format';
import { EstimateReadingResolver } from './EstimateReadingResolver';
import {
  type Chequeo, type Cifra, type Confianza, type Desglose, type GrupoDesglose, type LecturaCompleta, type Partida, type Texto,
  colorConfianza, colorSemaforo, fmtMoney, fmtNum, fmtSegundos,
} from '../lib/estimateReading';

/**
 * LA LECTURA ÚNICA DE UN ESTIMADO, ENTERA (mig 0498).
 *
 * Pedido del dueño (2026-10-07): "quiero que me pongas ese botón de análisis en cada estimado para
 * que lo tengamos y probarlos a ver qué tan rápido". Esta ventana es la segunda mitad de eso: lo que
 * la IA leyó, con la EVIDENCIA al lado de cada número (página y texto crudo) y el semáforo que la
 * validación en código le puso. No decide nada: muestra para que una persona pueda confiar o no.
 *
 * Va por PORTAL sobre <body> (ley UI 2026-10-05): `.main` es un contexto de apilamiento por debajo
 * del sidebar y un overlay fijo montado adentro queda tapado por el menú. `useDialog` pone Escape,
 * el ciclo del Tab y aria-modal.
 */
type Pestana = 'summary' | 'totals' | 'breakdown' | 'checks' | 'lines' | 'uncertain';
const PESTANAS: Pestana[] = ['summary', 'totals', 'breakdown', 'checks', 'lines', 'uncertain'];
/** Cuántas partidas se pintan de una: 200 filas es lo que un navegador de oficina mueve sin trabarse. */
const PASO = 200;

/** Los 12 totales del resumen financiero, en el orden en que se leen en el papel. */
const TOTALES = ['line_item_total', 'base_service_charges', 'sales_tax', 'overhead_profit', 'rcv', 'grand_total', 'depreciation', 'recoverable_depreciation', 'nonrecoverable_depreciation', 'paid_when_incurred', 'acv', 'deductible', 'prior_payments', 'net_claim'] as const;

/** Página en un badge mono: "p.3". Sin página no se inventa: "—". */
function Pag({ page }: { page: number | null | undefined }) {
  return <span className="rd-page">{page != null ? `p.${page}` : '—'}</span>;
}

/** El punto de confianza, con su nombre en el tooltip. */
function Conf({ c }: { c: Confianza | Partida['conf'] | null | undefined }) {
  const { t } = useI18n();
  if (!c) return null;
  return <span className="rd-conf" style={{ background: colorConfianza(c) }} title={t(`rd.conf.${c}`)} />;
}

/** Un texto leído con su evidencia: el valor pintado si la confianza es baja, la página y el punto. */
function ValorTexto({ v }: { v: Texto | null | undefined }) {
  if (!v) return <span className="muted">—</span>;
  const bajo = v.confidence === 'low' || v.confidence === 'unreadable';
  return (
    <span className="rd-valor" title={v.raw_text ?? undefined}>
      <span style={bajo ? { color: colorConfianza(v.confidence), fontWeight: 700 } : undefined}>{v.value ?? '—'}</span>
      <Pag page={v.page} /><Conf c={v.confidence} />
    </span>
  );
}

/** Una cifra leída con su evidencia, en dólares. */
function ValorCifra({ v }: { v: Cifra | null | undefined }) {
  if (!v) return <span className="muted">—</span>;
  const bajo = v.confidence === 'low' || v.confidence === 'unreadable';
  return (
    <span className="rd-valor" title={v.raw_text ?? undefined}>
      <span className="rd-mono" style={bajo ? { color: colorConfianza(v.confidence), fontWeight: 700 } : undefined}>{fmtMoney(v.value)}</span>
      <Pag page={v.page} /><Conf c={v.confidence} />
    </span>
  );
}

/** Una fila etiqueta → valor del resumen. */
function Fila({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rd-fila">
      <span className="rd-fila-k">{label}</span>
      <span className="rd-fila-v">{children}</span>
    </div>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="rd-bloque">
      <h4 className="rd-bloque-t">{titulo}</h4>
      {children}
    </section>
  );
}

/** Sí / No / — para los booleanos que pueden venir en null (el modelo no lo supo). */
function siNo(v: boolean | null | undefined, t: (k: string) => string): string {
  return v == null ? '—' : v ? t('rd.yes') : t('rd.no');
}

/** `onChanged`: alguien resolvió algo a mano (Resolver) y la lectura cambió: la lista de Files / Review Estimate debe recargar su chip. */
export function EstimateReadingModal({ fileId, fileName, onClose, onChanged }: { fileId: string; fileName: string | null; onClose: () => void; onChanged?: () => void }) {
  const { t } = useI18n();
  const dialogo = useDialog(onClose);
  const [data, setData] = useState<LecturaCompleta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Pestana>('summary');
  const [filtro, setFiltro] = useState('');
  /** "estructura › área" elegida en el desplegable; vacío = todas. */
  const [zona, setZona] = useState('');
  const [limite, setLimite] = useState(PASO);
  // El desglose lo calcula el servidor sobre la lectura (sin modelo); se pide al abrir la pestaña y al mover un interruptor.
  const [bdOpts, setBdOpts] = useState({ op: false, tax: false });
  const [bd, setBd] = useState<Desglose | null>(null);

  useEffect(() => {
    let vivo = true;
    setData(null); setErr(null);
    api.get<LecturaCompleta>(`/api/files/${fileId}/reading`)
      .then((r) => { if (vivo) setData(r); })
      .catch((e) => {
        if (!vivo) return;
        setErr(e instanceof ApiError ? (e.code === 'reading_not_found' ? t('rd.notFound') : e.message) : 'Error');
      });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  useEffect(() => {
    if (tab !== 'breakdown' || !data) return;
    let vivo = true;
    setBd(null);
    const sin = [bdOpts.op ? 'op' : null, bdOpts.tax ? 'tax' : null].filter(Boolean).join(',');
    api.get<{ breakdown: Desglose }>(`/api/files/${fileId}/reading/breakdown${sin ? `?without=${sin}` : ''}`).then((r) => { if (vivo) setBd(r.breakdown); }).catch(() => { if (vivo) setBd(null); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, bdOpts, fileId, data]);

  // Las zonas del desplegable salen de las partidas mismas (no de `structures`): es lo que de verdad
  // se puede filtrar, y así una partida con una estructura que el modelo no listó no se queda afuera.
  const zonas = useMemo(() => {
    const s = new Set<string>();
    for (const p of data?.reading.line_items ?? []) s.add(`${p.structure ?? '—'} › ${p.area ?? '—'}`);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Las partidas que no cuadran, para teñir su fila en la tabla: misma llave (número + descripción)
  // con la que el validador las reporta.
  const malas = useMemo(() => new Set((data?.validation.line_mismatches ?? []).map((m) => `${m.n ?? ''}|${m.desc}`)), [data]);

  const partidas = useMemo(() => {
    const todas = data?.reading.line_items ?? [];
    const q = filtro.trim().toLowerCase();
    return todas.filter((p) => {
      if (zona && `${p.structure ?? '—'} › ${p.area ?? '—'}` !== zona) return false;
      if (!q) return true;
      return `${p.n ?? ''} ${p.cat ?? ''} ${p.sel ?? ''} ${p.desc} ${p.structure ?? ''} ${p.area ?? ''}`.toLowerCase().includes(q);
    });
  }, [data, filtro, zona]);

  /** Baja TODO (meta + lectura + validación) como .json: es la evidencia completa, para auditar afuera. */
  function descargarJson() {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(fileName || `file-${fileId}`).replace(/\.pdf$/i, '')}.reading.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /** El nombre legible de un chequeo; los de estructura llevan el nombre de la estructura. */
  function nombreChequeo(c: Chequeo): string {
    if (c.check.startsWith('structure:')) return t('rd.check.structure', { name: c.check.slice('structure:'.length) });
    const k = `rd.check.${c.check}`;
    const s = t(k);
    return s === k ? c.check : s;
  }

  const meta = data?.meta;
  const r = data?.reading;
  const val = data?.validation;

  return createPortal(
    <div className="rd-overlay" onClick={onClose}>
      <div className="rd-panel" onClick={(e) => e.stopPropagation()} ref={dialogo.ref} {...dialogo.props}>
        {/* ── Cabecera: archivo, semáforo con sus razones, y la línea de "qué tan rápido". ── */}
        <div className="rd-head">
          <div className="rd-head-top">
            <ScanText size={15} strokeWidth={1.75} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <b className="rd-head-title" title={fileName ?? ''}>{t('rd.modalTitle')} · {fileName || `file-${fileId}`}</b>
            <span style={{ flex: 1 }} />
            {data && (
              <button className="btn ghost sm" onClick={descargarJson} title={t('rd.jsonHint')}>
                <FileJson size={13} strokeWidth={1.75} /> JSON
              </button>
            )}
            <button className="btn ghost sm" onClick={onClose} aria-label={t('common.close')}><X size={14} strokeWidth={1.75} /></button>
          </div>
          {meta && val && (
            <div className="rd-head-meta">
              <span className="rd-sema" style={{ color: colorSemaforo(val.semaforo), borderColor: colorSemaforo(val.semaforo) }}>
                <span className="rd-dot" style={{ background: colorSemaforo(val.semaforo) }} /> {t(`rd.sema.${val.semaforo}`)}
              </span>
              <span className="muted rd-meta-line">
                {t('rd.metaLine', {
                  date: meta.read_at ? new Date(meta.read_at).toLocaleString(localeUI(), { dateStyle: 'medium', timeStyle: 'short' }) : '—',
                  model: meta.model ?? '—', via: meta.via ?? '—', s: fmtSegundos(meta.latency_ms),
                  tin: fmtNum(meta.tokens_in, 0), tout: fmtNum(meta.tokens_out, 0),
                })}
              </span>
              {meta.copied && <span className="badge slate" style={{ fontSize: 'var(--fs-2xs)' }}><Copy size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rd.copied')}</span>}
              {meta.side && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t(`rd.side.${meta.side}`)}</span>}
              {(meta.resolved_count ?? 0) > 0 && <span className="badge slate" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rd.resolved', { n: meta.resolved_count ?? 0 })}</span>}
            </div>
          )}
          {val?.accepted && (
            <div className="rd-accepted">
              {t('rd.acceptedBanner', { name: val.accepted.by_name, date: new Date(val.accepted.at).toLocaleString(localeUI(), { dateStyle: 'medium', timeStyle: 'short' }), sema: t(`rd.sema.${val.accepted.semaforo_before}`), note: val.accepted.note })}
            </div>
          )}
          {val && val.reasons.length > 0 && (
            <ul className="rd-reasons">
              {val.reasons.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          )}
          {data && (
            <div className="rd-tabs" role="tablist">
              {PESTANAS.map((p) => {
                const n = p === 'lines' ? r?.line_items.length : p === 'checks' ? val?.checks.filter((c) => c.status === 'mismatch').length : p === 'uncertain' ? (r?.uncertain_fields.length ?? 0) + (r?.warnings.length ?? 0) : null;
                return (
                  <button key={p} role="tab" aria-selected={tab === p} className={`rd-tab${tab === p ? ' on' : ''}`} onClick={() => setTab(p)}>
                    {t(`rd.tab.${p}`)}{n != null && n > 0 && <span className="rd-tab-n">{n}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Cuerpo ── */}
        <div className="rd-body">
          {err ? (
            <div className="muted" style={{ padding: 16 }}><AlertTriangle size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom', color: 'var(--red)' }} /> {err}</div>
          ) : !data || !r || !val || !meta ? (
            <div className="muted" style={{ padding: 16 }}>{t('common.loadingDots')}</div>
          ) : tab === 'summary' ? (
            <div className="rd-kv">
              <Bloque titulo={t('rd.sec.document')}>
                <Fila label={t('rd.f.platform')}>{r.document.platform ?? '—'}</Fila>
                <Fila label={t('rd.f.estimateType')}>{r.document.estimate_type ?? '—'}</Fila>
                <Fila label={t('rd.f.estimateNumber')}>{[r.document.estimate_number, r.document.estimate_name].filter(Boolean).join(' · ') || '—'}</Fila>
                <Fila label={t('rd.f.estimateDate')}><ValorTexto v={r.document.estimate_date} /></Fila>
                <Fila label={t('rd.f.dates')}>{[r.document.date_created, r.document.date_revised].filter(Boolean).join(' → ') || '—'}</Fila>
                <Fila label={t('rd.f.pages')}>{r.document.pages_total ?? '—'}</Fila>
                <Fila label={t('rd.f.preparedBy')}>
                  {[r.document.prepared_by.company, r.document.prepared_by.person].filter(Boolean).join(' · ') || '—'}
                  {' '}<span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t(`rd.role.${r.document.prepared_by.role}`)}</span>
                  {' '}<span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: r.document.side === 'insurance' ? 'var(--red-soft)' : r.document.side === 'ours' ? 'var(--purple-soft)' : undefined, color: r.document.side === 'insurance' ? 'var(--red)' : r.document.side === 'ours' ? '#7c3aed' : undefined }}>{t(`rd.side.${r.document.side}`)}</span>
                </Fila>
                <Fila label={t('rd.f.adjuster')}>{[r.document.adjuster.name, r.document.adjuster.email, r.document.adjuster.phone].filter(Boolean).join(' · ') || '—'}</Fila>
                <Fila label={t('rd.f.estimator')}>{r.document.estimator ?? '—'}</Fila>
              </Bloque>
              <Bloque titulo={t('rd.sec.claim')}>
                <Fila label={t('rd.f.insured')}><ValorTexto v={r.claim.insured_name} />{r.claim.co_insured && <span className="muted"> · {r.claim.co_insured}</span>}</Fila>
                <Fila label={t('rd.f.address')}><ValorTexto v={r.claim.property_address} /><span className="muted"> {[r.claim.city, r.claim.state, r.claim.zip].filter(Boolean).join(', ')}</span></Fila>
                <Fila label={t('rd.f.claimNumber')}><ValorTexto v={r.claim.claim_number} /></Fila>
                <Fila label={t('rd.f.policyNumber')}><ValorTexto v={r.claim.policy_number} /></Fila>
                <Fila label={t('rd.f.dateOfLoss')}><ValorTexto v={r.claim.date_of_loss} /></Fila>
                <Fila label={t('rd.f.causeOfLoss')}>{r.claim.cause_of_loss ?? '—'}</Fila>
                <Fila label={t('rd.f.inspectionDate')}>{r.claim.inspection_date ?? '—'}</Fila>
              </Bloque>
              <Bloque titulo={t('rd.sec.pricing')}>
                <Fila label={t('rd.f.priceList')}><ValorTexto v={r.pricing.price_list_code} /></Fila>
                <Fila label={t('rd.f.priceListDate')}>{r.pricing.price_list_date ?? '—'}</Fila>
                <Fila label={t('rd.f.priceListLocation')}>{[r.pricing.price_list_location, r.pricing.zip].filter(Boolean).join(' · ') || '—'}</Fila>
              </Bloque>
              <Bloque titulo={t('rd.sec.policy')}>
                <Fila label={t('rd.f.settlementBasis')}>{r.policy.settlement_basis ?? '—'}</Fila>
                <Fila label={t('rd.f.coverages')}>
                  {r.policy.coverages.length === 0 ? '—' : (
                    <div className="rd-lista">{r.policy.coverages.map((c, i) => <div key={i}><b>{c.name}</b>: <ValorCifra v={c.limit} /></div>)}</div>
                  )}
                </Fila>
                <Fila label={t('rd.f.deductibles')}>
                  {r.policy.deductibles.length === 0 ? '—' : (
                    <div className="rd-lista">{r.policy.deductibles.map((d, i) => (
                      <div key={i}>
                        <b>{d.type ?? '—'}</b>: <ValorCifra v={d.amount} />
                        {d.percent != null && <span className="muted"> · {fmtNum(d.percent)}%</span>}
                        {d.coverage && <span className="muted"> · {d.coverage}</span>}
                        {d.applied_in_calculation != null && <span className="muted"> · {d.applied_in_calculation ? t('rd.f.applied') : t('rd.f.notApplied')}</span>}
                      </div>
                    ))}</div>
                  )}
                </Fila>
                {r.policy.notes && <Fila label={t('rd.f.notes')}>{r.policy.notes}</Fila>}
              </Bloque>
              <Bloque titulo={t('rd.sec.flags')}>
                <Fila label={t('rd.f.roofFull')}>{siNo(r.flags.roof_full_replacement, t)}</Fila>
                <Fila label={t('rd.f.buildingCode')}>{siNo(r.flags.has_building_code_items, t)}</Fila>
                {r.flags.code_upgrade_items.length > 0 && (
                  <Fila label={t('rd.f.codeItems')}>
                    <div className="rd-lista">{r.flags.code_upgrade_items.map((x, i) => <div key={i}>{x.desc} <span className="rd-mono">{fmtMoney(x.amount)}</span> <Pag page={x.page} /></div>)}</div>
                  </Fila>
                )}
              </Bloque>
              <Bloque titulo={`${t('rd.sec.structures')} (${r.structures.length})`}>
                {r.structures.length === 0 ? <span className="muted">—</span> : r.structures.map((s, i) => (
                  <div key={i} className="rd-estructura">
                    <div><b>{s.name}</b>{s.coverage && <span className="muted"> · {s.coverage}</span>}</div>
                    <div className="rd-areas">
                      {s.areas.map((a, j) => (
                        <div key={j} className="rd-area">
                          <span title={a.path ?? undefined}>{a.name}</span>
                          <span className="muted">{a.line_count != null ? t('rd.nItems', { n: a.line_count }) : ''}</span>
                          <span className="rd-mono">{fmtMoney(a.rcv_subtotal)}</span>
                          <Pag page={a.page} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </Bloque>
            </div>
          ) : tab === 'totals' ? (
            <div className="rd-stack">
              <div className="rd-scroll">
                <table className="rd-table">
                  <thead><tr><th>{t('rd.col.concept')}</th><th className="num">{t('rd.col.value')}</th><th>{t('rd.col.printedAs')}</th><th>{t('rd.col.page')}</th><th>{t('rd.col.conf')}</th></tr></thead>
                  <tbody>
                    {TOTALES.map((k) => {
                      const c = r.financial_summary[k];
                      if (!c || ((k === 'base_service_charges' || k === 'paid_when_incurred') && c.value == null)) return null; // lecturas v2 no traen el ajuste; y sin BSC no se lista
                      const bajo = c.confidence === 'low' || c.confidence === 'unreadable';
                      return (
                        <tr key={k}>
                          <td>{t(`rd.tot.${k}`)}</td>
                          <td className="num" style={bajo ? { color: colorConfianza(c.confidence), fontWeight: 700 } : undefined}>{fmtMoney(c.value)}</td>
                          <td className="muted rd-raw">{c.raw_text ?? '—'}</td>
                          <td><Pag page={c.page} /></td>
                          <td><Conf c={c.confidence} /> <span className="muted">{t(`rd.conf.${c.confidence}`)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <h4 className="rd-bloque-t">{t('rd.sec.byCoverage')} ({r.financial_summary.by_coverage.length})</h4>
              {r.financial_summary.by_coverage.length > 0 && (
                <div className="rd-scroll">
                  <table className="rd-table">
                    <thead><tr><th>{t('rd.col.coverage')}</th><th className="num">RCV</th><th className="num">ACV</th><th className="num">{t('rd.col.dep')}</th><th className="num">{t('rd.col.deductible')}</th><th className="num">{t('rd.col.net')}</th><th>{t('rd.col.page')}</th></tr></thead>
                    <tbody>
                      {r.financial_summary.by_coverage.map((c, i) => (
                        <tr key={i}><td>{c.coverage}{c.manual && <span className="rd-manual">{t('rd.manual')}</span>}</td><td className="num">{fmtMoney(c.rcv)}</td><td className="num">{fmtMoney(c.acv)}</td><td className="num">{fmtMoney(c.depreciation)}</td><td className="num">{fmtMoney(c.deductible)}</td><td className="num">{fmtMoney(c.net_claim)}</td><td><Pag page={c.page} /></td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <h4 className="rd-bloque-t">{t('rd.sec.byStructure')} ({r.financial_summary.by_structure.length})</h4>
              {r.financial_summary.by_structure.length > 0 && (
                <div className="rd-scroll">
                  <table className="rd-table">
                    <thead><tr><th>{t('rd.col.structure')}</th><th className="num">{t('rd.col.printedRcv')}</th><th className="num">{t('rd.col.linesSum')}</th><th>{t('rd.col.page')}</th></tr></thead>
                    <tbody>
                      {r.financial_summary.by_structure.map((s, i) => {
                        const suma = val.sums.by_structure[s.structure];
                        return <tr key={i}><td>{s.structure}</td><td className="num">{fmtMoney(s.rcv)}</td><td className="num muted">{suma != null ? fmtMoney(suma) : '—'}</td><td><Pag page={s.page} /></td></tr>;
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {t('rd.sumsLine', { n: val.sums.lines_count, rcv: fmtMoney(val.sums.lines_rcv) })}
              </div>
            </div>
          ) : tab === 'breakdown' ? (
            <div className="rd-stack">
              <div className="rd-bd-opts">
                <label className="rd-check"><input type="checkbox" checked={bdOpts.op} onChange={(e) => setBdOpts((o) => ({ ...o, op: e.target.checked }))} /> {t('rd.bd.withoutOp')}</label>
                <label className="rd-check"><input type="checkbox" checked={bdOpts.tax} onChange={(e) => setBdOpts((o) => ({ ...o, tax: e.target.checked }))} /> {t('rd.bd.withoutTax')}</label>
                {bd && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rd.bd.rates', { tax: bd.rates.tax_pct ?? '—', op: bd.rates.op_pct ?? '—' })}</span>}
              </div>
              {!bd ? <div className="muted">{t('common.loadingDots')}</div> : (
                <>
                  <div className="rd-bd-totals">
                    <Kpi label={t('rd.bd.base')} v={bd.totals.base} />
                    <Kpi label={t('rd.bd.tax')} v={bd.totals.tax} dim={Boolean(bd.options.sinTax)} />
                    <Kpi label="O&P" v={bd.totals.op} dim={Boolean(bd.options.sinOp)} />
                    {bd.totals.bsc > 0 && <Kpi label={t('rd.bd.bsc')} v={bd.totals.bsc} />}
                    <Kpi label={t('rd.bd.total')} v={bd.totals.rcv} strong />
                    {(bd.options.sinOp || bd.options.sinTax) && <Kpi label={t('rd.bd.fullTotal')} v={bd.totals.rcv_full} />}
                  </div>
                  {bd.notes.length > 0 && <ul className="rd-bd-notes">{bd.notes.map((n, i) => <li key={i} className="muted">{n}</li>)}</ul>}
                  <GrupoTabla titulo={t('rd.bd.byCategory')} grupos={bd.by_category} />
                  <GrupoTabla titulo={t('rd.bd.byZone')} grupos={bd.by_zone} />
                  <GrupoTabla titulo={t('rd.bd.byActivity')} grupos={bd.by_activity} />
                </>
              )}
            </div>
          ) : tab === 'checks' ? (
            <div className="rd-stack">
              <div className="rd-scroll">
                <table className="rd-table">
                  <thead><tr><th>{t('rd.col.check')}</th><th className="num">{t('rd.col.docSays')}</th><th className="num">{t('rd.col.calculated')}</th><th className="num">{t('rd.col.delta')}</th><th>{t('rd.col.status')}</th><th>{t('rd.col.note')}</th></tr></thead>
                  <tbody>
                    {val.checks.length === 0 && <tr><td colSpan={6} className="muted">{t('rd.noChecks')}</td></tr>}
                    {val.checks.map((c, i) => (
                      <tr key={i}>
                        <td>{nombreChequeo(c)}{c.severity === 'high' && <span className="muted"> · {t('rd.sevHigh')}</span>}</td>
                        <td className="num">{fmtMoney(c.source_value)}</td>
                        <td className="num">{fmtMoney(c.calculated_value)}</td>
                        <td className="num" style={c.status === 'mismatch' ? { color: 'var(--red)', fontWeight: 700 } : undefined}>{c.delta != null ? fmtMoney(c.delta) : '—'}</td>
                        <td>
                          {c.status === 'ok' && <span className="badge green" style={{ fontSize: 'var(--fs-2xs)' }}><Check size={11} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {t('rd.st.ok')}</span>}
                          {c.status === 'mismatch' && <span className={`badge ${c.severity === 'high' ? 'red' : 'orange'}`} style={{ fontSize: 'var(--fs-2xs)' }}><AlertTriangle size={11} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {t('rd.st.mismatch')}</span>}
                          {c.status === 'skipped' && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rd.st.skipped')}</span>}
                        </td>
                        <td className="muted rd-raw">{c.note ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <EstimateReadingResolver fileId={fileId} data={data} onApplied={(d) => { setData(d); onChanged?.(); }} />
              <h4 className="rd-bloque-t">{t('rd.sec.lineMismatches')} ({val.line_mismatches.length})</h4>
              {val.line_mismatches.length > 0 && (
                <div className="rd-scroll">
                  <table className="rd-table">
                    <thead><tr><th>#</th><th>{t('rd.col.desc')}</th><th className="num">{t('rd.col.printed')}</th><th className="num">{t('rd.col.calculated')}</th><th className="num">{t('rd.col.delta')}</th><th>{t('rd.col.page')}</th></tr></thead>
                    <tbody>
                      {val.line_mismatches.map((m, i) => (
                        <tr key={i}><td className="rd-mono">{m.n ?? '—'}</td><td>{m.desc}</td><td className="num">{fmtMoney(m.printed)}</td><td className="num">{fmtMoney(m.calculated)}</td><td className="num" style={{ color: 'var(--red)' }}>{fmtMoney(m.printed - m.calculated)}</td><td><Pag page={m.page} /></td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <h4 className="rd-bloque-t">{t('rd.sec.lowConfidence')} ({val.key_fields_low_confidence.length})</h4>
              {val.key_fields_low_confidence.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {val.key_fields_low_confidence.map((k, i) => <span key={i} className="badge orange" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}>{k}</span>)}
                </div>
              )}
            </div>
          ) : tab === 'lines' ? (
            <div className="rd-stack">
              <div className="rd-filter">
                <input className="input sm" value={filtro} onChange={(e) => { setFiltro(e.target.value); setLimite(PASO); }} placeholder={t('rd.filterPh')} style={{ flex: '1 1 180px', minWidth: 0 }} />
                <select className="input sm" value={zona} onChange={(e) => { setZona(e.target.value); setLimite(PASO); }} style={{ flex: '0 1 260px', minWidth: 0 }}>
                  <option value="">{t('rd.allZones')}</option>
                  {zonas.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
                  {t('rd.showing', { shown: Math.min(limite, partidas.length), total: partidas.length, all: r.line_items.length })}
                </span>
              </div>
              <div className="rd-scroll">
                <table className="rd-table rd-lines">
                  <thead>
                    <tr>
                      <th>#</th><th>{t('rd.col.zone')}</th><th>{t('rd.col.code')}</th><th>{t('rd.col.desc')}</th>
                      <th className="num">{t('rd.col.qty')}</th><th className="num">{t('rd.col.unitPrice')}</th><th className="num">{t('rd.col.tax')}</th><th className="num">O&amp;P</th>
                      <th className="num">RCV</th><th className="num">{t('rd.col.dep')}</th><th className="num">ACV</th><th>{t('rd.col.page')}</th><th>{t('rd.col.conf')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partidas.length === 0 && <tr><td colSpan={13} className="muted">{t('rd.noLines')}</td></tr>}
                    {partidas.slice(0, limite).map((p, i) => (
                      <tr key={`${p.n ?? 'x'}-${i}`} className={[malas.has(`${p.n ?? ''}|${p.desc}`) ? 'rd-row-bad' : '', p.struck ? 'rd-row-struck' : ''].filter(Boolean).join(' ') || undefined} title={p.struck ? t('rd.struck') : undefined}>
                        <td className="rd-mono">{p.n ?? '—'}</td>
                        <td className="rd-zona" title={`${p.structure ?? '—'} › ${p.area ?? '—'}`}>{p.area ?? p.structure ?? '—'}</td>
                        <td className="rd-mono muted">{[p.cat, p.sel].filter(Boolean).join(' ')}{p.act ? ` ${p.act}` : ''}</td>
                        <td className="rd-desc">{p.struck && <span className="rd-struck-tag">{t('rd.struck')}</span>}{p.desc}{p.manual && <span className="rd-manual">{t('rd.manual')}</span>}{p.age_life && <span className="muted"> · {p.age_life}</span>}</td>
                        <td className="num">{fmtNum(p.qty)}{p.unit ? <span className="muted"> {p.unit}</span> : null}</td>
                        <td className="num" title={p.up_rm != null || p.up_rp != null ? `${t('rd.col.remove')} ${fmtMoney(p.up_rm ?? 0)} + ${t('rd.col.replace')} ${fmtMoney(p.up_rp ?? 0)}` : undefined}>{fmtMoney(p.up)}</td>
                        <td className="num muted">{fmtMoney(p.tax)}</td>
                        <td className="num muted">{fmtMoney(p.op)}</td>
                        <td className="num" style={{ fontWeight: 700 }}>{fmtMoney(p.rcv)}</td>
                        <td className="num muted">{fmtMoney(p.dep)}{p.dep_pct != null && <span> ({fmtNum(p.dep_pct)}%)</span>}</td>
                        <td className="num">{fmtMoney(p.acv)}</td>
                        <td><Pag page={p.page} /></td>
                        <td><Conf c={p.conf} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {partidas.length > limite && (
                <button className="btn ghost sm" onClick={() => setLimite((n) => n + PASO)} style={{ alignSelf: 'center' }}>
                  {t('rd.showMore', { n: Math.min(PASO, partidas.length - limite) })}
                </button>
              )}
            </div>
          ) : (
            <div className="rd-stack">
              <h4 className="rd-bloque-t">{t('rd.sec.uncertain')} ({r.uncertain_fields.length})</h4>
              {r.uncertain_fields.length > 0 && (
                <div className="rd-scroll">
                  <table className="rd-table">
                    <thead><tr><th>{t('rd.col.field')}</th><th>{t('rd.col.page')}</th><th>{t('rd.col.possibleValue')}</th><th>{t('rd.col.reason')}</th></tr></thead>
                    <tbody>
                      {r.uncertain_fields.map((u, i) => (
                        <tr key={i}><td className="rd-mono">{u.field}</td><td><Pag page={u.page} /></td><td>{u.possible_value ?? '—'}</td><td className="rd-raw">{u.reason}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <h4 className="rd-bloque-t">{t('rd.sec.warnings')} ({r.warnings.length})</h4>
              {r.warnings.length > 0 ? (
                <ul className="rd-reasons" style={{ margin: 0 }}>
                  {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              ) : <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rd.noWarnings')}</span>}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Una cifra del desglose: etiqueta chica arriba, monto en mono; `dim` = apagada por un interruptor (sin O&P / sin impuesto). */
function Kpi({ label, v, dim, strong }: { label: string; v: number; dim?: boolean; strong?: boolean }) {
  return (
    <div className={`rd-kpi${dim ? ' dim' : ''}${strong ? ' strong' : ''}`}>
      <div className="rd-kpi-l">{label}</div>
      <div className="rd-kpi-v">{fmtMoney(v)}</div>
    </div>
  );
}

/** Tabla de un agrupamiento del desglose (categoría, zona o actividad): partidas, base, impuesto, O&P y RCV. */
function GrupoTabla({ titulo, grupos }: { titulo: string; grupos: GrupoDesglose[] }) {
  const { t } = useI18n();
  if (!grupos.length) return null;
  return (
    <div>
      <h4 className="rd-bloque-t">{titulo} ({grupos.length})</h4>
      <div className="rd-scroll">
        <table className="rd-table">
          <thead><tr><th>{titulo}</th><th className="num">{t('rd.bd.lines')}</th><th className="num">{t('rd.bd.base')}</th><th className="num">{t('rd.bd.tax')}</th><th className="num">O&amp;P</th><th className="num">RCV</th></tr></thead>
          <tbody>
            {grupos.map((g) => (
              <tr key={g.key}>
                <td>{g.label}</td><td className="num muted">{g.lines}</td><td className="num">{fmtMoney(g.base)}</td><td className="num muted">{fmtMoney(g.tax)}</td><td className="num muted">{fmtMoney(g.op)}</td><td className="num" style={{ fontWeight: 700 }}>{fmtMoney(g.rcv)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
